import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@screentone/addon-auth-wrapper';
import { Alert, AlertType, IconInfo, Responsive, Subnav, Wrapper } from '@screentone/core';
import classnames from 'classnames';

import { useAlert } from 'contexts/alert/useAlert';
import { Artifact } from 'data/generated/graphql';
import { usePublicationSettings } from 'hooks/publication-settings';
import { useIsAdmin, useIsPublisher } from 'hooks/roles';
import env from 'utils/env';
import styles from './UcsSubnav.module.scss';

export const UcsSubnav = () => {
  const { currentProperty } = useAuth();
  const { pathname } = useLocation();
  const { alertState, removeAlert } = useAlert();
  const { data: publicationSettingsResp } = usePublicationSettings();

  const propertyPath = currentProperty ? `/${currentProperty}` : '';

  const handleAlertDismiss = () => {
    removeAlert();
  };
  const isPublisher = useIsPublisher();
  const isAdmin = useIsAdmin();

  const publicationSettings = publicationSettingsResp?.publicationSetting;
  const arePagesEnabled = publicationSettings?.enabledArtifacts.some((artifact) => artifact === Artifact.Page);
  const areCollectionsEnabled = publicationSettings?.enabledArtifacts.some(
    (artifact) => artifact === Artifact.Collection
  );
  const areOffPlatformEnabled = publicationSettings?.enabledArtifacts.some(
    (artifact) => artifact === Artifact.OffPlatform
  );
  const areItpIssuesEnabled = publicationSettings?.enabledArtifacts.some((artifact) => artifact === Artifact.ItpIssue);
  const areNewslettersEnabled = publicationSettings?.enabledArtifacts.some(
    (artifact) => artifact === Artifact.Newsletter
  );
  const areBannersEnabled = publicationSettings?.enabledArtifacts.some((artifact) => artifact === Artifact.Banner);
  const areNewswiresEnabled = publicationSettings?.enabledArtifacts.some(
    (artifact) => artifact === Artifact.MultiCollection
  );
  const areLayoutsEnabled = publicationSettings?.enabledArtifacts.some((artifact) => artifact === Artifact.Layout);

  return (
    <Wrapper className={styles.ucsSubnavWrapper} id="ucs-subnav-wrapper">
      <Responsive sm lg className={styles.ucsSubnav}>
        <Subnav margin={{ bottom: 'none' }} data-testid="ucs-subnav-tabs" className={styles.ucsSubnavTabs}>
          {isPublisher && arePagesEnabled && (
            <Subnav.Item
              componentEl={Link}
              to={`${propertyPath}/`}
              active={currentProperty ? [`/${currentProperty}/`, `/${currentProperty}`].includes(pathname) : false}
              data-testid="ucs-subnav-pages-tab"
            >
              Pages
            </Subnav.Item>
          )}
          {areCollectionsEnabled && (
            <Subnav.Item
              componentEl={Link}
              to={`${propertyPath}/collections`}
              active={currentProperty ? pathname === `/${currentProperty}/collections` : false}
              data-testid="ucs-subnav-collections-tab"
            >
              Collections
            </Subnav.Item>
          )}
          {areOffPlatformEnabled && (
            <Subnav.Item
              componentEl={Link}
              to={`${propertyPath}/off-platform`}
              active={currentProperty ? pathname === `/${currentProperty}/off-platform` : false}
              data-testid="ucs-subnav-off-platform-tab"
            >
              Off-Platform
            </Subnav.Item>
          )}
          {isPublisher && areItpIssuesEnabled && (
            <Subnav.Item
              componentEl={Link}
              to={`${propertyPath}/issues`}
              active={pathname === `/${currentProperty}/issues`}
              data-testid="ucs-subnav-itp-issues-tab"
            >
              ITP Issues
            </Subnav.Item>
          )}
          {isPublisher && areNewslettersEnabled && (
            <Subnav.Item
              componentEl={Link}
              to={`${propertyPath}/newsletters`}
              active={pathname === `/${currentProperty}/newsletters`}
              data-testid="ucs-subnav-news-letters-tab"
            >
              Newsletters
            </Subnav.Item>
          )}
          {isPublisher && areBannersEnabled && (
            <Subnav.Item
              componentEl={Link}
              to={`${propertyPath}/banners`}
              active={pathname === `/${currentProperty}/banners`}
              data-testid="ucs-subnav-banners-tab"
            >
              Banners
            </Subnav.Item>
          )}
          {isAdmin && areLayoutsEnabled && (
            <Subnav.Item
              componentEl={Link}
              to={`${propertyPath}/site-config/layouts`}
              active={currentProperty ? pathname.startsWith(`/${currentProperty}/site-config/layouts`) : false}
              data-testid="ucs-subnav-layouts-tab"
            >
              Layouts
            </Subnav.Item>
          )}
          {isPublisher && areNewswiresEnabled && (
            <Subnav.Item
              componentEl={Link}
              to={`${propertyPath}/multicollection/${env.UCS_COLLECTION_PREFIX}_NEWSWIRES_MULTICOLLECTION`}
              active={pathname.startsWith(`/${currentProperty}/multicollection`)}
              data-testid="ucs-subnav-newswires-tab"
            >
              Newswires
            </Subnav.Item>
          )}
        </Subnav>
      </Responsive>
      {alertState.message && (
        <Alert
          className={classnames(styles.alert, alertState.wrapNewlines ? styles.wrapText : '')}
          icon={IconInfo}
          type={alertState.type ? (alertState.type as AlertType) : undefined}
          onDismiss={handleAlertDismiss}
          autoDismiss={alertState.autoDismiss}
          data-testid="ucs-subnav-alert"
        >
          {alertState.message}
          {alertState.link && (
            <Alert.Action href={alertState.link.url}>{alertState.link.text || 'More information'}</Alert.Action>
          )}
        </Alert>
      )}
    </Wrapper>
  );
};
